import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Trash2, CheckCircle, XCircle, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  pending: 'bg-amber-500/15 text-amber-400 border-amber-500/40',
  approved: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/40',
  rejected: 'bg-red-500/15 text-red-400 border-red-500/40',
};

export default function AdminDeletionRequestsPage() {
  const [rows, setRows] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [busyId, setBusyId] = useState(null);

  const fetchRows = useCallback(() => {
    axios.get(`${API}/admin/deletion-requests`)
      .then((r) => { setRows(r.data?.items || []); setPendingCount(r.data?.pending_count || 0); })
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const decide = async (row, action) => {
    const verb = action === 'approve' ? 'APPROVE and permanently delete' : 'REJECT (nothing changes)';
    if (!window.confirm(`${verb}?\n\n${row.label}\nRequested by ${row.requested_by_name}`)) return;
    setBusyId(row.id);
    try {
      const r = await axios.post(`${API}/admin/deletion-requests/${row.id}/${action}`);
      toast.success(r.data?.message || 'Done');
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  const visible = rows.filter((r) => filter === 'all' || r.status === filter);

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 space-y-4 max-w-4xl mx-auto">
        <div className="border-b border-border pb-4 flex items-center gap-3">
          <Trash2 className="w-6 h-6 text-primary" strokeWidth={1.5} />
          <div>
            <h1 className="text-2xl font-barlow tracking-widest text-foreground">DELETION REQUESTS</h1>
            <p className="text-xs text-muted-foreground mt-1">
              Operations staff cannot delete shifts/events — they request it here and you decide.
            </p>
          </div>
          {pendingCount > 0 && (
            <Badge className="ml-auto bg-amber-500/15 text-amber-400 border border-amber-500/40" data-testid="del-req-pending-badge">
              {pendingCount} pending
            </Badge>
          )}
        </div>

        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList className="bg-muted/40 border border-border rounded-sm">
            <TabsTrigger value="pending" data-testid="del-filter-pending">Pending ({rows.filter((r) => r.status === 'pending').length})</TabsTrigger>
            <TabsTrigger value="approved" data-testid="del-filter-approved">Approved</TabsTrigger>
            <TabsTrigger value="rejected" data-testid="del-filter-rejected">Rejected</TabsTrigger>
            <TabsTrigger value="all" data-testid="del-filter-all">All</TabsTrigger>
          </TabsList>
        </Tabs>

        {loading ? (
          <p className="py-12 text-center text-sm text-muted-foreground">Loading…</p>
        ) : visible.length === 0 ? (
          <p className="py-12 text-center text-sm text-muted-foreground" data-testid="del-req-empty">Nothing here.</p>
        ) : (
          <div className="space-y-2">
            {visible.map((r) => (
              <Card key={r.id} className="bg-card border-border rounded-sm" data-testid={`del-req-${r.id}`}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{r.label}</p>
                      <p className="text-xs text-muted-foreground">
                        By <strong>{r.requested_by_name}</strong> · {new Date(r.created_at).toLocaleString('en-GB')}
                        {r.reason ? ` · Reason: ${r.reason}` : ''}
                        {r.decided_by_name ? ` · Decided by ${r.decided_by_name}` : ''}
                      </p>
                    </div>
                    <Badge className={`border text-[10px] uppercase tracking-wider shrink-0 ${STATUS_STYLE[r.status] || ''}`}>
                      {r.status === 'pending' ? <Clock className="w-3 h-3 mr-1" /> : null}{r.status}
                    </Badge>
                  </div>
                  {r.status === 'pending' && (
                    <div className="flex gap-2">
                      <Button
                        size="sm" disabled={busyId === r.id}
                        onClick={() => decide(r, 'approve')}
                        className="rounded-sm bg-red-600 hover:bg-red-500 text-white text-xs uppercase tracking-wider"
                        data-testid={`approve-del-${r.id}`}
                      >
                        <CheckCircle className="w-3.5 h-3.5 mr-1" /> Approve Delete
                      </Button>
                      <Button
                        size="sm" variant="outline" disabled={busyId === r.id}
                        onClick={() => decide(r, 'reject')}
                        className="rounded-sm text-xs uppercase tracking-wider border-border"
                        data-testid={`reject-del-${r.id}`}
                      >
                        <XCircle className="w-3.5 h-3.5 mr-1" /> Reject Delete
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
